import React from 'react';
import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import { TextField, Button, MenuItem } from '@mui/material';
import { useContactForm } from './useContactForm';
import { HeaderNarrow } from 'ui/components/panes/Header/HeaderNarrow';
import { CONTACT_CATEGORY_CHOICES, CONTACT_CATEGORY_LABEL } from 'domain/valueObjects/Contact/Contact';
import { mqMin } from 'utils/responsive';

const Container = styled.section`
    background-color: var(--lightblue);
    padding: 40px 0 40px;
    min-height: calc(100svh - 90px);

    ${mqMin('SP')} {
        min-height: calc(100svh - 70px);
    }
`;

const FormContainer = styled.div`
    width: 88.37209302325581%;
    max-width: 380px;
    margin: 0 auto;
    background-color: #fff;
    border-radius: 8px;
    padding: 32px 24px;

    ${mqMin('NARROW')} {
        width: 600px;
        max-width: initial;
        padding: 48px;
    }
`;

const Title = styled.h2`
    text-align: center;
    font-size: 1.625rem;
    margin-bottom: 32px;
    color: var(--dark-blue);

    ${mqMin('NARROW')} {
        font-size: 1.75rem;
        margin-bottom: 40px;
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const StyledTextField = styled(TextField)`
    .MuiOutlinedInput-root {
        background-color: #fff;
    }
`;

const StyledButton = styled(Button)`
    margin-top: 16px;
    background-color: var(--dark-blue);
    color: #fff;
    font-size: 1rem;
    padding: 12px 0;
    border-radius: 4px;
    text-transform: none;

    &:hover {
        background-color: var(--dark-blue);
        opacity: 0.9;
    }

    &:disabled {
        background-color: #ccc;
    }
`;

const categoryOptions = [
    {
        value: CONTACT_CATEGORY_CHOICES.CONTACT_CATEGORY_SERVICE,
        label: CONTACT_CATEGORY_LABEL[CONTACT_CATEGORY_CHOICES.CONTACT_CATEGORY_SERVICE],
    },
    {
        value: CONTACT_CATEGORY_CHOICES.CONTACT_CATEGORY_TECHNICAL,
        label: CONTACT_CATEGORY_LABEL[CONTACT_CATEGORY_CHOICES.CONTACT_CATEGORY_TECHNICAL],
    },
    {
        value: CONTACT_CATEGORY_CHOICES.CONTACT_CATEGORY_BILLING,
        label: CONTACT_CATEGORY_LABEL[CONTACT_CATEGORY_CHOICES.CONTACT_CATEGORY_BILLING],
    },
    {
        value: CONTACT_CATEGORY_CHOICES.CONTACT_CATEGORY_OTHER,
        label: CONTACT_CATEGORY_LABEL[CONTACT_CATEGORY_CHOICES.CONTACT_CATEGORY_OTHER],
    },
];

export const ContactForm: React.FC = () => {
    const { control, errors, submit, loading, complete } = useContactForm();

    return (
        <>
            <HeaderNarrow />
            <Container>
                {complete ? (
                    <FormContainer>
                        <Title>お問い合わせ完了</Title>
                        <p>
                            お問い合わせいただき誠にありがとうございます。
                            <br />
                            内容を確認の上、担当者よりご連絡いたします。
                        </p>
                    </FormContainer>
                ) : (
                    <FormContainer>
                        <Title>お問い合わせ</Title>
                        <Form onSubmit={submit}>
                            <Controller
                                name="email"
                                control={control}
                                render={({ field }) => (
                                    <StyledTextField
                                        {...field}
                                        label="メールアドレス"
                                        error={!!errors.email}
                                        helperText={errors.email?.message}
                                        fullWidth
                                    />
                                )}
                            />
                            <Controller
                                name="name"
                                control={control}
                                render={({ field }) => (
                                    <StyledTextField
                                        {...field}
                                        label="氏名"
                                        error={!!errors.name}
                                        helperText={errors.name?.message}
                                        fullWidth
                                    />
                                )}
                            />
                            <Controller
                                name="category"
                                control={control}
                                render={({ field }) => (
                                    <StyledTextField
                                        {...field}
                                        select
                                        label="お問い合わせカテゴリ"
                                        error={!!errors.category}
                                        helperText={errors.category?.message}
                                        fullWidth
                                    >
                                        {categoryOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </StyledTextField>
                                )}
                            />
                            <Controller
                                name="content"
                                control={control}
                                render={({ field }) => (
                                    <StyledTextField
                                        {...field}
                                        label="お問い合わせ内容"
                                        error={!!errors.content}
                                        helperText={errors.content?.message}
                                        multiline
                                        rows={6}
                                        fullWidth
                                    />
                                )}
                            />
                            <StyledButton type="submit" variant="contained" disabled={loading}>
                                {loading ? '送信中...' : '送信する'}
                            </StyledButton>
                        </Form>
                    </FormContainer>
                )}
            </Container>
        </>
    );
};
